/**
 * Global error handler for WalletConnect session errors
 * Detects "No matching key. session topic doesn't exist" and similar errors
 * Automatically clears stale sessions and reloads the page
 */

let hasReloaded = false;
let reloadTimeout: NodeJS.Timeout | null = null;

/**
 * Check if an error message indicates a WalletConnect session issue
 */
export const isWalletConnectSessionError = (error: any): boolean => {
  const errorMessage = String(error?.message || error || '').toLowerCase();
  
  return (
    errorMessage.includes('session topic') ||
    errorMessage.includes('no matching key') ||
    errorMessage.includes('session expired') ||
    errorMessage.includes('restore connection') ||
    errorMessage.includes('pairing expired') ||
    errorMessage.includes('subscriber not found') ||
    errorMessage.includes('socket stalled') ||
    errorMessage.includes('connection failed')
  );
};

/**
 * Handle WalletConnect session error with page reload
 */
export const handleWalletConnectSessionError = (error: any): void => {
  if (!isWalletConnectSessionError(error) || hasReloaded) {
    return;
  }

  console.error('[Global Error Handler] WalletConnect session error detected:', error);
  
  hasReloaded = true;

  // Clear timeout if already set
  if (reloadTimeout) {
    clearTimeout(reloadTimeout);
  }

  // Show user-friendly message (optional)
  console.log('[Global Error Handler] Reloading page to restore connection...');

  // Reload page after a short delay
  reloadTimeout = setTimeout(() => {
    window.location.reload();
  }, 1500);
};

/**
 * Initialize global error handlers
 * Call this once when your app starts
 */
export const initializeGlobalErrorHandlers = (): void => {
  // Reset reload flag on page load
  hasReloaded = false;

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isWalletConnectSessionError(event.reason)) {
      event.preventDefault(); // Prevent default error logging
      handleWalletConnectSessionError(event.reason);
    }
  });

  // Handle global errors
  window.addEventListener('error', (event: ErrorEvent) => {
    if (isWalletConnectSessionError(event.error || event.message)) {
      event.preventDefault(); // Prevent default error logging
      handleWalletConnectSessionError(event.error || event.message);
    }
  });

  // Intercept console.error to catch logged errors
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    // Check if any argument is a WalletConnect error
    const hasSessionError = args.some(arg => isWalletConnectSessionError(arg));
    
    if (hasSessionError) {
      handleWalletConnectSessionError(args[0]);
    }
    
    // Call original console.error
    originalConsoleError.apply(console, args);
  };

  console.log('[Global Error Handler] Initialized WalletConnect error detection');
};

/**
 * Cleanup function (optional, for when component unmounts)
 */
export const cleanupGlobalErrorHandlers = (): void => {
  if (reloadTimeout) {
    clearTimeout(reloadTimeout);
    reloadTimeout = null;
  }
  hasReloaded = false;
};
